import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchApi } from '../../../utils/api';
import { KeywordData } from '../../../types/webTrafficAnalytics';
import { GeneratedBlogTitle, ContentCluster } from '../../../types/topicalAuthorityGenerator';

interface TopicalAuthorityTabProps {
  onGeneratePost: (titleData: any) => void;
}

const TopicalAuthorityTab: React.FC<TopicalAuthorityTabProps> = ({ onGeneratePost }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for input options
  const [sourceType, setSourceType] = useState<'analytics' | 'custom'>('analytics');
  const [maxTitles, setMaxTitles] = useState<number>(50);
  
  // State for custom input
  const [keyword, setKeyword] = useState('');
  const [product, setProduct] = useState('');
  const [service, setService] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  
  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState<KeywordData[] | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // State for generation
  const [generating, setGenerating] = useState(false);
  const [generatedTitles, setGeneratedTitles] = useState<GeneratedBlogTitle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  
  // Fetch analytics data when using analytics source
  useEffect(() => {
    if (sourceType === 'analytics' && user) {
      fetchAnalyticsData();
    }
    setRequestId(null);
    setGeneratedTitles(null);
  }, [sourceType, user]);
  
  const fetchAnalyticsData = async () => {
    if (!user) return;
    
    setLoadingAnalytics(true);
    setError(null);
    
    try {
      const response = await fetchApi(`/api/marketing/web-traffic-analytics?userId=${user.id}`);
      if (response.data?.combined) {
        setAnalyticsData(response.data.combined);
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to fetch analytics data. Please try again.');
    } finally {
      setLoadingAnalytics(false);
    }
  };
  
  const handleGenerate = async () => {
    if (!user) {
      setError('You must be logged in to generate content.');
      return;
    }
    
    if (sourceType === 'custom' && !keyword.trim()) {
      setError('Please enter a keyword for custom input.');
      return;
    }
    
    if (sourceType === 'analytics' && !analyticsData) {
      setError('Please connect to web analytics or select custom input.');
      return;
    }
    
    setGenerating(true);
    setError(null);
    setSuccess(null);
    setGeneratedTitles([]);
    setRequestId(null);
    
    try {
      const requestBody: any = {
        userId: user.id,
        sourceType,
        maxTitles
      };
      
      if (sourceType === 'custom') {
        requestBody.customInput = {
          keyword: keyword.trim(),
          product: product.trim(),
          service: service.trim(),
          industry: industry.trim(),
          targetAudience: targetAudience.trim()
        };
      } else if (sourceType === 'analytics' && analyticsData) {
        requestBody.analyticsData = analyticsData;
      }
      
      const response = await fetchApi('/api/marketing/topical-authority', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
      
      if (response.contentStructure || response.standaloneArticles) {
        const allTitles: GeneratedBlogTitle[] = [];
        
        if (response.contentStructure) {
          response.contentStructure.forEach((cluster: ContentCluster) => {
            allTitles.push(cluster.pillarArticle);
            allTitles.push(...cluster.clusterArticles);
          });
        }
        
        if (response.standaloneArticles) {
          allTitles.push(...response.standaloneArticles);
        }
        
        setGeneratedTitles(allTitles);
        setRequestId(response.requestId);
        setSuccess('Topical authority content generated successfully!');
      } else {
        setError('Failed to generate content. Please try again.');
      }
    } catch (err) {
      console.error('Error generating topical authority content:', err);
      setError('Failed to generate content. Please try again.');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleExportCSV = async () => {
    if (!user) {
      setError('You must be logged in to export content.');
      return;
    }
    
    if (!requestId) {
      setError('Please generate content first before exporting to CSV.');
      return;
    }
    
    try {
      const requestBody: any = {
        userId: user.id,
        requestId: requestId
      };
      
      const response = await fetch('/api/marketing/topical-authority-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.status} ${response.statusText}`);
      }
      
      const csvText = await response.text();
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'topical-authority-content.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSuccess('CSV file downloaded successfully!');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV. Please try again.');
    }
  };

  const handleGeneratePostClick = (title: GeneratedBlogTitle) => {
    const isPillar = title.suggestedContentLength.includes('1,500-2,500');
    onGeneratePost({
      topic: title.title,
      description: `Write a comprehensive ${isPillar ? 'pillar' : 'cluster'} article about ${title.title}`,
      keywords: [title.primaryKeyword, ...title.secondaryKeywords],
      length: isPillar ? 'Long (1500-2000 words)' : 'Medium (800-1200 words)',
      targetAudience: targetAudience || 'General readers',
      enableFAQ: isPillar,
      enableTables: isPillar,
      enableLists: true
    });
  };
  
  return (
    <div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">{success}</h3>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Source</h3>
          <div className="flex space-x-4">
            <button
              type="button"
              className={`px-4 py-2 rounded-md text-sm font-medium ${sourceType === 'analytics' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
              onClick={() => setSourceType('analytics')}
            >
              Use Analytics Data
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-md text-sm font-medium ${sourceType === 'custom' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}
              onClick={() => setSourceType('custom')}
            >
              Custom Input
            </button>
          </div>
        </div>
        
        {sourceType === 'analytics' ? (
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">Analytics Data</h4>
            <p className="text-gray-600 mb-4 text-sm">
              Generate content based on your existing web traffic analytics data.
            </p>
            
            {loadingAnalytics ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading analytics data...</span>
              </div>
            ) : analyticsData && analyticsData.length > 0 ? (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impressions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analyticsData.slice(0, 10).map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.keyword}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.totalClicks}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.totalImpressions}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.googlePosition || item.bingPosition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="px-4 py-2 text-xs text-gray-500 bg-gray-50">
                  Showing top 10 of {analyticsData.length} keywords
                </p>
              </div>
            ) : (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No analytics data</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Connect your Google Search Console or Bing Webmaster Tools account.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/marketing/web-traffic-analytics')}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Connect Analytics Accounts
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Custom Input</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Keyword, Product, or Service *
                </label>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., digital marketing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product (Optional)
                </label>
                <input
                  type="text"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., premium running shoes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service (Optional)
                </label>
                <input
                  type="text"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., SEO optimization"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry (Optional)
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., fitness, finance"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience (Optional)
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., small business owners"
                />
              </div>
            </div>
          </div>
        )}
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Titles to Generate: <span className="font-bold">{maxTitles}</span>
          </label>
          <input
            type="range"
            min="10"
            max="100"
            value={maxTitles}
            onChange={(e) => setMaxTitles(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>10</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className={`inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${generating ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {generating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              'Generate Topical Authority Content'
            )}
          </button>
          
          {generatedTitles && generatedTitles.length > 0 && (
            <button
              type="button"
              onClick={handleExportCSV}
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Export as CSV
            </button>
          )}
        </div>
      </div>
      
      {generatedTitles && generatedTitles.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Generated Content</h3>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {generatedTitles.length} Titles
              </span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Primary Keyword</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Secondary Keywords</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {generatedTitles.map((title, index) => {
                  const isPillar = title.suggestedContentLength.includes('1,500-2,500');
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" style={{ maxWidth: '300px' }}>
                        {title.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {title.primaryKeyword}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {isPillar ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Pillar
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Cluster
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {title.secondaryKeywords.join(', ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleGeneratePostClick(title)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                        >
                          <svg className="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                          Create Post
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicalAuthorityTab;
